import classNames from 'classnames';
import { useAuthorization, useProjectAccessStatus } from 'modules/auth';
import { getCurrentUser } from 'modules/data';
import { useProjectIdentity } from 'modules/routes';
import PropTypes from 'prop-types';
import { FaEyeSlash, FaKey } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import InterviewArchiveDisplay from './InterviewArchiveDisplay';
import InterviewImage from './InterviewImage';
import ThumbnailMetadata from './ThumbnailMetadata';

/**
 * Shows interview preview content, with project attribution on umbrella routes.
 * @param {Object} props - Interview, owning project, locale, and expansion state.
 * @returns {React.ReactElement} Access-aware title, preview image, and metadata.
 */
export default function InterviewPreviewInner({
    interview,
    project,
    locale,
    isExpanded,
}) {
    const { projectAccessGranted } = useProjectAccessStatus(project);
    const { isAuthorized } = useAuthorization();
    const { isUmbrella } = useProjectIdentity();
    const currentUser = useSelector(getCurrentUser);
    const permitted = currentUser?.interview_permissions.some(
        (p) => p.interview_id === interview.id
    );

    return (
        <>
            <InterviewImage interview={interview} project={project} />

            <div
                className={classNames(
                    'InterviewCard-title',
                    isExpanded ? 'u-mt' : 'u-mt-small'
                )}
            >
                {interview.workflow_state === 'unshared' && (
                    <FaEyeSlash className="u-mr-tiny" />
                )}
                {interview.workflow_state === 'restricted' && (
                    <FaKey
                        className="u-mr-tiny"
                        style={{
                            filter: permitted ? 'opacity(40%)' : 'opacity(90%)',
                        }}
                    />
                )}
                {!interview.short_title && !interview.anonymous_title ? (
                    <Skeleton />
                ) : projectAccessGranted &&
                  (interview.workflow_state === 'public' ||
                      (interview.workflow_state === 'restricted' &&
                          permitted) ||
                      isAuthorized(interview, 'update')) ? (
                    interview.short_title?.[locale]
                ) : (
                    interview.anonymous_title[locale]
                )}
            </div>

            {isUmbrella && <InterviewArchiveDisplay project={project} />}

            {!isExpanded && <ThumbnailMetadata interview={interview} />}
        </>
    );
}

InterviewPreviewInner.propTypes = {
    interview: PropTypes.object.isRequired,
    project: PropTypes.object,
    locale: PropTypes.string.isRequired,
    isExpanded: PropTypes.bool.isRequired,
};
